import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Eye, PenLine, X } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDate, SIG_STATUS } from "../../lib/labels";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function Firmas() {
  const [params, setParams] = useSearchParams();
  const estado = params.get("estado") || "";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: items, isLoading, refetch } = useQuery({
    queryKey: ["signatures", estado],
    queryFn: async () => (await api.get("/legal/signatures", { params: { status: estado || undefined } })).data,
  });

  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["legal-dashboard"] }); };

  const event = async (s, ev, payload = {}) => {
    try {
      await api.post(`/legal/signatures/${s.id}/event`, { event: ev, ...payload });
      toast.success("Evento registrado");
      refresh();
    } catch (e) {
      const reasons = e?.response?.data?.detail?.reasons;
      if (reasons) toast.error("Evento bloqueado", { description: reasons.map((r) => r.message).join(" · ") });
      else toast.error(apiError(e));
    }
  };

  const download = async (s) => {
    try {
      const r = await api.get(`/legal/contracts/${s.contract_id}/document.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("No se pudo descargar"); }
  };

  return (
    <div data-testid="firmas-page">
      <PageHeader title="Firmas electrónicas" subtitle="Conector DocuSign en modo demo. Los envíos requieren confirmación humana; la firma completada activa contratos, obligaciones e hitos." />
      <div className="mb-4">
        <Select value={estado} onValueChange={(v) => setParams(v === "todos" ? {} : { estado: v })}>
          <SelectTrigger className="h-8 w-52" data-testid="signature-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos</SelectItem>{Object.entries(SIG_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando firmas…</p>
      ) : !items?.length ? (
        <EmptyState title="Sin solicitudes de firma" description="Las solicitudes se preparan desde la pestaña Firma de cada contrato aprobado." testid="signatures-empty" />
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="ts-card p-4" data-testid={`signature-card-${s.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Chip map={SIG_STATUS} value={s.status} testid={`sig-status-${s.id}`} />
                <button onClick={() => navigate(`/legal/contratos/${s.contract_id}?tab=firma`)} className="min-w-0 flex-1 truncate text-left text-sm font-medium text-blue-800 hover:underline" data-testid={`sig-contract-${s.id}`}>
                  {s.subject}
                </button>
                <span className="text-xs text-slate-400">{s.adapter} · v{s.document_version} · expira {fmtDate(s.expires_at)}</span>
              </div>
              {s.contract && <p className="mt-1 text-xs text-slate-500">{s.contract.contract_number} — {s.contract.title}</p>}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {s.signers.map((sg) => (
                  <span key={sg.email} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span className={`h-2 w-2 rounded-full ${sg.status === "firmado" ? "bg-emerald-500" : "bg-slate-300"}`} />
                    {sg.order}. {sg.name} {sg.signed_at ? `(${fmtDate(sg.signed_at)})` : ""}
                  </span>
                ))}
              </div>
              {s.rejection_reason && <p className="mt-1 text-xs text-red-600">Rechazada: {s.rejection_reason}</p>}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["enviado", "visualizado", "parcialmente_firmado"].includes(s.status) && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => event(s, "visualizado")} data-testid={`sig-view-${s.id}`}><Eye className="mr-1 h-3.5 w-3.5" /> Simular visualización</Button>
                    {s.signers.filter((sg) => sg.status !== "firmado").map((sg) => (
                      <Button key={sg.email} size="sm" variant="outline" onClick={() => event(s, "firmar", { signer_email: sg.email })} data-testid={`sig-sign-${s.id}-${sg.order}`}>
                        <PenLine className="mr-1 h-3.5 w-3.5" /> Simular firma de {sg.name.split(" ")[0]}
                      </Button>
                    ))}
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { const m = window.prompt("Motivo del rechazo:"); if (m) event(s, "rechazar", { motivo: m }); }} data-testid={`sig-reject-${s.id}`}><X className="mr-1 h-3.5 w-3.5" /> Simular rechazo</Button>
                  </>
                )}
                {s.status === "completado" && (
                  <Button size="sm" variant="outline" onClick={() => download(s)} data-testid={`sig-download-${s.id}`}><Download className="mr-1 h-3.5 w-3.5" /> Documento firmado + certificado</Button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-2 border-t border-slate-100 pt-2">
                {(s.events || []).slice(-4).map((e, i) => (
                  <span key={i} className="text-[10px] text-slate-400">{fmtDate(e.at)} — {e.event}{e.detail ? `: ${e.detail}` : ""}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
